import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { Building2, LockKeyhole, Mail } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { saveOfflineSession } from "@/features/offline/offline-db";
import { api } from "@/lib/api";

export function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [recoveryOpen, setRecoveryOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const client = useQueryClient();
  const cached = client.getQueryData<Awaited<ReturnType<typeof api.session>>>([
    "auth-session",
  ]);
  const mutation = useMutation({
    mutationFn: api.login,
    onSuccess: async (session) => {
      await saveOfflineSession(session);
      client.setQueryData(["auth-session"], session);
      toast.success("Sesión institucional iniciada");
      const state = location.state as { from?: string } | null;
      navigate(
        session.user?.mustChangePassword
          ? "/cambiar-contrasena"
          : (state?.from ?? "/mapa"),
        { replace: true },
      );
    },
    onError: (error) => toast.error(error.message),
  });
  const recovery = useMutation({
    mutationFn: api.requestRecovery,
    onSuccess: (result) => {
      toast.success(result.message);
      setRecoveryOpen(false);
    },
    onError: (error) => toast.error(error.message),
  });
  if (cached?.authenticated)
    return (
      <Navigate
        to={cached.user?.mustChangePassword ? "/cambiar-contrasena" : "/mapa"}
        replace
      />
    );
  return (
    <main className="grid min-h-screen bg-sidebar lg:grid-cols-[minmax(320px,.8fr)_minmax(520px,1.2fr)]">
      <section className="hidden flex-col justify-between border-r border-white/10 p-10 text-sidebar-foreground lg:flex">
        <div className="flex items-center gap-4">
          <div className="grid size-12 place-items-center border border-cyan-200/70 text-2xl font-bold text-white">
            S
          </div>
          <div>
            <strong className="block text-xl tracking-[0.08em] text-white">
              SISCOM
            </strong>
            <span className="text-xs text-cyan-100/70">
              Ayuntamiento de Los Cabos
            </span>
          </div>
        </div>
        <div className="max-w-lg">
          <h1 className="text-4xl font-bold leading-tight text-white">
            Coordinación municipal con trazabilidad territorial.
          </h1>
          <p className="mt-4 text-base leading-relaxed text-cyan-50/75">
            Acceso interno para las dependencias autorizadas. Las acciones,
            evidencias y aprobaciones quedan registradas en la bitácora
            institucional.
          </p>
        </div>
        <p className="text-xs text-sidebar-foreground/60">
          Sistema institucional · Uso interno
        </p>
      </section>
      <section className="grid place-items-center bg-background p-5 sm:p-8">
        <div className="w-full max-w-md">
          <div className="mb-7 flex items-center gap-3 lg:hidden">
            <div className="grid size-10 place-items-center bg-sidebar text-lg font-bold text-white">
              S
            </div>
            <div>
              <strong className="block tracking-[0.08em]">SISCOM</strong>
              <span className="text-xs text-muted-foreground">
                Los Cabos · B.C.S.
              </span>
            </div>
          </div>
          <Card>
            <CardHeader>
              <div className="mb-3 grid size-11 place-items-center rounded-lg bg-secondary text-primary">
                <Building2 />
              </div>
              <CardTitle className="text-2xl">Acceso institucional</CardTitle>
              <CardDescription>
                Ingresa con la cuenta asignada por la administración de SISCOM.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form
                className="grid gap-5"
                onSubmit={(event) => {
                  event.preventDefault();
                  mutation.mutate({ email, password });
                }}
              >
                <div className="grid gap-2">
                  <Label htmlFor="email">Correo institucional</Label>
                  <div className="relative">
                    <Mail className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      id="email"
                      type="email"
                      autoComplete="username"
                      required
                      value={email}
                      onChange={(event) => setEmail(event.target.value)}
                      className="pl-9"
                      placeholder="usuario@siscom.local"
                    />
                  </div>
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="password">Contraseña</Label>
                  <div className="relative">
                    <LockKeyhole className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      id="password"
                      type="password"
                      autoComplete="current-password"
                      required
                      minLength={8}
                      value={password}
                      onChange={(event) => setPassword(event.target.value)}
                      className="pl-9"
                    />
                  </div>
                </div>
                <Button className="w-full" disabled={mutation.isPending}>
                  {mutation.isPending ? "Verificando…" : "Ingresar a SISCOM"}
                </Button>
                <Button
                  type="button"
                  variant="link"
                  className="h-auto"
                  onClick={() => setRecoveryOpen((value) => !value)}
                >
                  No puedo acceder
                </Button>
                {recoveryOpen ? (
                  <div className="grid gap-3 rounded-md border bg-secondary/40 p-4">
                    <p className="text-xs text-muted-foreground">
                      Captura tu correo institucional. El administrador recibirá
                      una solicitud sin exponer si la cuenta existe.
                    </p>
                    <Button
                      type="button"
                      variant="outline"
                      disabled={!email || recovery.isPending}
                      onClick={() => recovery.mutate(email)}
                    >
                      {recovery.isPending
                        ? "Enviando…"
                        : "Solicitar recuperación"}
                    </Button>
                  </div>
                ) : null}
              </form>
            </CardContent>
          </Card>
        </div>
      </section>
    </main>
  );
}
